import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import { useAuth } from '../../contexts/AuthContext';
import MemberNav from './MemberNav';
import { CardSkeleton } from './LoadingSkeleton';
import { logError } from '../../utils/logger';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import { speciesLabel } from '../../utils/memberStats';
import {
  ApprovedCatch, CompetitionEvent, filterCatchesByYear,
  computeSeasonSummary, computePersonalSpeciesBests, findBiggestFish,
} from '../../utils/seasonStatsUtils';

const client = generateClient({ authMode: 'userPool' });

interface PersonalStatsViewProps {
  onNavigate: (page: 'dashboard' | 'profile' | 'competitions' | 'catches' | 'my-stats' | 'my-fishing' | 'season-championship' | 'home') => void;
  embedded?: boolean;
}

const PersonalStatsView: React.FC<PersonalStatsViewProps> = ({ onNavigate, embedded }) => {
  const { userId } = useAuth();
  const [catches, setCatches] = useState<ApprovedCatch[]>([]);
  const [events, setEvents] = useState<CompetitionEvent[]>([]);
  const [member, setMember] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      // Fetch member record via the narrow server-side projection
      const profileResult: any = await (client.queries as any).getOwnMemberProfile({});
      const profileResponse = parseLambdaResponse(profileResult?.data);
      const m = profileResponse?.success ? profileResponse.own : null;
      setMember(m);

      // Fetch catches with pagination
      let allCatches: any[] = [];
      let nextToken: string | null = null;
      do {
        const result: any = await client.models.CatchSubmission.list({
          filter: { memberSub: { eq: userId } },
          ...(nextToken ? { nextToken } : {}),
        });
        allCatches = allCatches.concat(result.data || []);
        nextToken = result.nextToken ?? null;
      } while (nextToken);

      const approved: ApprovedCatch[] = allCatches
        .filter((c: any) => c.status === 'APPROVED')
        .map((c: any) => ({
          id: c.id, eventId: c.eventId, species: c.species, lengthCm: c.lengthCm,
          competitorName: c.competitorName, keptOrReleased: c.keptOrReleased, submissionTimestamp: c.submissionTimestamp,
        }));
      setCatches(approved);

      // Fetch competition events
      let allEvents: any[] = [];
      let evtToken: string | null = null;
      do {
        const result: any = await client.models.Event.list({
          filter: { eventType: { eq: 'COMPETITION' } },
          ...(evtToken ? { nextToken: evtToken } : {}),
        });
        allEvents = allEvents.concat(result.data || []);
        evtToken = result.nextToken ?? null;
      } while (evtToken);

      setEvents(allEvents.map((e: any) => ({ id: e.id, title: e.title, eventDate: e.eventDate })));
    } catch (err: any) {
      logError('PersonalStatsView', err);
      setError('Failed to load your statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [userId]);

  const year = new Date().getFullYear();
  const seasonCatches = filterCatchesByYear(catches, events, year);
  const summary = computeSeasonSummary(seasonCatches, events);
  const speciesBests = computePersonalSpeciesBests(seasonCatches);
  const biggest = findBiggestFish(seasonCatches);

  // Family members
  let familyMembers: Array<{ name: string }> = [];
  if (member?.membershipType === 'FAMILY' && member?.familyMembers) {
    try { familyMembers = typeof member.familyMembers === 'string' ? JSON.parse(member.familyMembers) : member.familyMembers || []; } catch { /* ignore */ }
  }

  const renderStats = (label: string, myCatches: ApprovedCatch[]) => {
    const s = computeSeasonSummary(myCatches, events);
    const b = findBiggestFish(myCatches);
    const bests = computePersonalSpeciesBests(myCatches);

    if (myCatches.length === 0) {
      return <p className="text-sm text-gray-500 italic">No catches recorded for {label}</p>;
    }

    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-lg font-bold">{s.totalCatches}</div>
            <div className="text-xs text-gray-500">Catches</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-lg font-bold">{b ? `${b.lengthCm}cm` : '—'}</div>
            <div className="text-xs text-gray-500">Biggest Fish</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-lg font-bold">{s.keptPercent}% / {s.releasedPercent}%</div>
            <div className="text-xs text-gray-500">Kept / Released</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-lg font-bold">{s.competitionsHeld}</div>
            <div className="text-xs text-gray-500">Competitions</div>
          </div>
        </div>
        {bests.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead><tr><th className="px-3 py-1 text-left text-xs text-gray-500">Species</th><th className="px-3 py-1 text-right text-xs text-gray-500">Personal Best</th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {bests.map((b: any) => (
                  <tr key={b.species}><td className="px-3 py-1">{speciesLabel(b.species)}</td><td className="px-3 py-1 text-right font-medium">{b.lengthCm}cm</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    if (embedded) return <CardSkeleton />;
    return (
      <div className="min-h-screen bg-gray-50">
        <MemberNav currentPage={"dashboard" as any} onNavigate={onNavigate} />
        <div className="max-w-7xl mx-auto px-4 py-8 text-center text-gray-600">Loading your statistics…</div>
      </div>
    );
  }

  if (error) {
    if (embedded) return <div className="bg-red-50 border border-red-200 rounded-md p-4"><p className="text-red-800">{error}</p><button onClick={fetchData} className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium">Retry</button></div>;
    return (
      <div className="min-h-screen bg-gray-50">
        <MemberNav currentPage={"dashboard" as any} onNavigate={onNavigate} />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-red-800">{error}</p>
            <button onClick={fetchData} className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium">Retry</button>
          </div>
        </div>
      </div>
    );
  }

  const content = (
    <>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">My Stats — {year} Season</h1>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">My Season Summary</h3>
        {renderStats('you', seasonCatches.filter((c: ApprovedCatch) => !familyMembers.some((fm: any) => fm.name === c.competitorName)))}
      </div>

      {familyMembers.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Family Members</h3>
          {familyMembers.map((fm: any) => (
            <div key={fm.name} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
              <h4 className="font-medium text-gray-900 mb-3">{fm.name}</h4>
              {renderStats(fm.name, seasonCatches.filter((c: ApprovedCatch) => c.competitorName === fm.name))}
            </div>
          ))}
        </div>
      )}
    </>
  );

  if (embedded) return content;

  return (
    <div className="min-h-screen bg-gray-50">
      <MemberNav currentPage={"dashboard" as any} onNavigate={onNavigate} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {content}
      </div>
    </div>
  );
};

export default PersonalStatsView;
